import { useState } from "react";
import { Pressable } from "react-native";
import { Card, Text, View } from "@src/components";
import type { WasteSenseSite } from "../types";
import type { ImageProvider } from "../imageProvider";
import { computeDailyActivity } from "../dailyActivity";
import { countPotentialSiteIncidents, countPrioritySiteIncidents } from "../priority";
import { formatSiteHeading } from "../siteDisplay";
import { DailyActivityTrend } from "./DailyActivityTrend";
import { EventsTable } from "./EventsTable";

interface SiteIncidentLogsProps {
  sites: readonly WasteSenseSite[];
  imageProvider: ImageProvider;
}

/**
 * "Incident logs by site" section — one collapsible block per site
 * containing a daily activity trend chart and the per-event log table.
 *
 * The first site starts expanded so the page has something to show
 * without requiring a click; the rest are collapsed to keep the
 * initial viewport readable on long-window reports.
 */
export function SiteIncidentLogs({ sites, imageProvider }: SiteIncidentLogsProps) {
  return (
    <View gap="md">
      <Text textStyle="h4" color="text">
        Incident logs by site
      </Text>
      <View gap="md">
        {sites.map((site, idx) => (
          <SiteBlock
            key={site.assets[0] ?? site.title}
            site={site}
            imageProvider={imageProvider}
            initiallyOpen={idx === 0}
          />
        ))}
      </View>
    </View>
  );
}

interface SiteBlockProps {
  site: WasteSenseSite;
  imageProvider: ImageProvider;
  initiallyOpen: boolean;
}

function SiteBlock({ site, imageProvider, initiallyOpen }: SiteBlockProps) {
  const [open, setOpen] = useState(initiallyOpen);
  const series = computeDailyActivity(site);
  const observations = site.observations ?? site.events ?? 0;
  const potentialIncidents = countPotentialSiteIncidents(site);
  const priorityIncidents = countPrioritySiteIncidents(site);
  const cameraSummary = site.assets.length === 1 ? site.assets[0] : `${site.assets.length} cameras`;

  return (
    <Card px="md" py="md">
      <Pressable onPress={() => setOpen((v) => !v)} accessibilityRole="button" accessibilityState={{ expanded: open }}>
        <View flexDirection="row" gap="md" alignItems="flex-start">
          <View flex={1} gap="xxs">
            <Text textStyle="sm/semibold" color="text">
              {formatSiteHeading(site.title)}
            </Text>
          </View>
          <View flexDirection="row" flexWrap="wrap" gap="md" alignItems="center">
            <Text textStyle="xs/regular" color="textMuted">
              {observations.toLocaleString()} observations
            </Text>
            <Text textStyle="xs/regular" color="textMuted">
              Potential {potentialIncidents.toLocaleString()} · Priority {priorityIncidents.toLocaleString()}
            </Text>
            <Text textStyle="xs/semibold" color="text">
              {cameraSummary}
            </Text>
            <Text textStyle="xs/semibold" color="textMuted">
              {open ? "▾" : "▸"}
            </Text>
          </View>
        </View>
      </Pressable>

      {open ?
        <View gap="md" mt="md">
          <Divider />
          <DailyActivityTrend series={series} />
          <Divider />
          <EventsTable rows={site.rows ?? []} imageProvider={imageProvider} site={site} />
        </View>
      : null}
    </Card>
  );
}

function Divider() {
  return <View h={1} bg="borderCard" />;
}
